import { Link } from 'react-router-dom';
import { ArrowRight, Check, FolderHeart, Layers3, Lock, PenTool, RefreshCcw, ShieldCheck, Sparkles, Star, Wand2 } from 'lucide-react';
import { InvitationPreview } from '../components/invitation/InvitationPreview';
import { Footer } from '../components/layout/Footer';

const showcaseInvitations = [
  {
    id: 'showcase-romantic',
    couple_names: { name1: 'Megan', name2: 'Wium' },
    date: 'Sunday, December 6, 2026',
    time: '6:00 PM',
    location: 'Paarl',
    dress_code: 'Black Tie',
    style: 'Romantic Classic',
    tone: 'Romantic',
    context_scene: 'Golden vineyard estate'
  },
  {
    id: 'showcase-modern',
    couple_names: { name1: 'Olivia', name2: 'Liam' },
    date: 'Friday, September 22, 2025',
    time: '6:00 PM',
    location: 'Urban Loft Gallery',
    dress_code: 'Cocktail',
    style: 'Soft Modern',
    tone: 'Modern',
    context_scene: 'Architectural city celebration'
  },
  {
    id: 'showcase-boho',
    couple_names: { name1: 'Luna', name2: 'River' },
    date: 'Sunday, May 1, 2025',
    time: '2:00 PM',
    location: 'Wildflower Meadow',
    dress_code: 'Garden Chic',
    style: 'Floral Boho',
    tone: 'Playful',
    context_scene: 'Countryside ceremony at golden hour'
  }
];

const processCards = [
  {
    label: 'Step 1',
    title: 'Set the mood',
    body: 'Choose a tone, scene, and level of formality so the invitation feels intentionally yours.',
    icon: Sparkles
  },
  {
    label: 'Step 2',
    title: 'Shape the invitation',
    body: 'Refine names, date, dress code, and venue inside a calmer, premium editor experience.',
    icon: PenTool
  },
  {
    label: 'Step 3',
    title: 'Save, export, deliver',
    body: 'Keep polished invitations in your library and move straight into download or checkout flows.',
    icon: Wand2
  }
];

const dashboardStats = [
  { label: 'Saved invitations', value: '24', detail: 'Across styles and edits' },
  { label: 'Ready to export', value: '8', detail: 'Print and story formats' },
  { label: 'Average turnaround', value: '4 min', detail: 'From brief to polished draft' }
];

const dashboardRows = [
  { title: 'Megan & Wium', style: 'Romantic Classic', status: 'Ready for print', detail: 'A5 PDF, story export, gold palette' },
  { title: 'Olivia & Liam', style: 'Soft Modern', status: 'Awaiting checkout', detail: 'Square social pack, mobile preview' },
  { title: 'Luna & River', style: 'Floral Boho', status: 'Draft v3', detail: 'Needs final venue line review' }
];

const appScreens = [
  {
    title: 'Wealthsimple-style onboarding',
    text: 'A calm, single-focus iOS flow for style, mood, and invitation setup.'
  },
  {
    title: 'Invitation library dashboard',
    text: 'A cleaner saved-invitations home with filters, status cards, and quick actions.'
  },
  {
    title: 'Soft pricing and checkout',
    text: 'Tiimo tone with a clearer final review flow inspired by cleaner payment journeys.'
  }
];

const trustSignals = [
  { icon: Lock, label: 'No credit card required to start' },
  { icon: RefreshCcw, label: 'Cancel or change your plan anytime' },
  { icon: ShieldCheck, label: 'Your designs stay private and secure' },
];

function SectionHeading({ eyebrow, title, body, align = 'left' }) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-2xl'}>
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#8A7A6A]">
        {eyebrow}
      </p>
      <h2 className="font-serif text-4xl leading-tight text-[#211D1A] md:text-5xl">
        {title}
      </h2>
      {body && (
        <p className="mt-5 text-base leading-8 text-[#6C6258] md:text-lg">
          {body}
        </p>
      )}
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#F8F4EE] text-[#211D1A]">

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-[#E8E0D6] bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.96),_rgba(248,244,238,0.88)_40%,_rgba(241,229,233,0.9)_100%)] pt-28 md:pt-32">
        <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.45),transparent_38%,rgba(225,209,198,0.22)_70%,transparent)]" />
        <div className="absolute left-[-8%] top-24 h-64 w-64 rounded-full bg-[#F3D7DC]/40 blur-3xl" />
        <div className="absolute bottom-6 right-[-10%] h-72 w-72 rounded-full bg-[#E7DED2]/70 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl gap-14 px-6 pb-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:pb-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#DCCFBE] bg-white/75 px-4 py-2 text-sm text-[#6C6258] shadow-soft backdrop-blur">
              <Sparkles size={16} className="text-[#C6A14B]" />
              Invitation-first design, not generic templates
            </div>

            <h1 className="mt-8 max-w-3xl font-serif text-5xl leading-[0.95] tracking-[-0.03em] text-[#211D1A] md:text-7xl">
              A softer, more premium home for beautifully crafted invitations.
            </h1>

            <p className="mt-8 max-w-2xl text-lg leading-8 text-[#6C6258] md:text-xl">
              VowVisionsAI now leads with the invitations themselves. Create polished designs, manage them in a cleaner library, and move through a calmer flow that feels closer to a luxury product than a template tool.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/create"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1E1A17] px-7 py-4 text-base font-medium text-white transition hover:bg-[#0E0C0B] hover:scale-[1.02] active:scale-100"
              >
                Start creating <ArrowRight size={18} />
              </Link>
              <Link
                to="/library"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#DCCFBE] bg-white/85 px-7 py-4 text-base font-medium text-[#211D1A] transition hover:border-[#C7B49A] hover:bg-white hover:scale-[1.02] active:scale-100"
              >
                Explore your library <FolderHeart size={18} />
              </Link>
            </div>

            {/* Trust signals */}
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {trustSignals.map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5 text-xs text-[#8A7A6A]">
                  <Icon size={13} className="text-[#C6A14B]" />
                  {label}
                </span>
              ))}
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {dashboardStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-[28px] border border-[#E4D9CB] bg-white/80 p-5 shadow-soft backdrop-blur transition hover:shadow-md hover:-translate-y-0.5"
                >
                  <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#8A7A6A]">{stat.label}</p>
                  <p className="mt-3 font-serif text-3xl text-[#211D1A]">{stat.value}</p>
                  <p className="mt-2 text-sm text-[#6C6258]">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[660px]">
            <div className="rounded-[40px] border border-[#E8DDD1] bg-white/82 p-5 shadow-[0_32px_80px_-40px_rgba(45,35,25,0.35)] backdrop-blur md:p-6">
              <div className="rounded-[34px] bg-[linear-gradient(180deg,#FCF7F0_0%,#F5EBDD_100%)] p-5 md:p-6">
                <div className="flex flex-col gap-4 border-b border-[#E6D9C9] pb-5 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#8A7A6A]">Showcase</p>
                    <p className="mt-2 max-w-md font-serif text-[2rem] leading-tight text-[#211D1A]">
                      A cleaner invitation-first hero.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-[#7E6D5C]">
                      Product-led visuals
                    </span>
                    <span className="rounded-full bg-[#F3E8D3] px-3 py-1 text-xs font-medium text-[#8E6B1C]">
                      Softer hierarchy
                    </span>
                  </div>
                </div>
                <div className="mt-6 grid gap-5">
                  <div className="rounded-[30px] border border-[#E7DACA] bg-white px-5 py-6 shadow-[0_20px_60px_-40px_rgba(45,35,25,0.35)]">
                    <div className="mb-5 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#8A7A6A]">Featured invitation</p>
                        <p className="mt-2 font-serif text-2xl text-[#211D1A]">
                          {showcaseInvitations[0].couple_names.name1} & {showcaseInvitations[0].couple_names.name2}
                        </p>
                      </div>
                      <div className="text-right text-sm text-[#7A6D60]">
                        <p>{showcaseInvitations[0].style}</p>
                        <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-[#A78B5D]">
                          {showcaseInvitations[0].dress_code}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-center overflow-hidden rounded-[26px] bg-[linear-gradient(180deg,#FFFCF8_0%,#F7EFE6_100%)] px-2 py-4">
                      <InvitationPreview invitation={showcaseInvitations[0]} size="large" />
                    </div>
                  </div>
                  <div className="rounded-[28px] border border-[#E7DACA] bg-white p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#8A7A6A]">What makes VowVisionsAI different</p>
                    <ul className="mt-4 space-y-3 text-sm leading-7 text-[#5E564F]">
                      <li className="flex items-start gap-3">
                        <Check size={16} className="mt-1 shrink-0 text-[#C6A14B]" />
                        The hero shows your actual invitation output instead of decorative filler.
                      </li>
                      <li className="flex items-start gap-3">
                        <Check size={16} className="mt-1 shrink-0 text-[#C6A14B]" />
                        One dominant card creates a clearer reading order on desktop and mobile.
                      </li>
                      <li className="flex items-start gap-3">
                        <Check size={16} className="mt-1 shrink-0 text-[#C6A14B]" />
                        The layout stays calmer when the invitation remains the only showcase focal point.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Showcase ── */}
      <section className="border-b border-[#E8E0D6] bg-[#FBF8F3] py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            eyebrow="Showcase the product"
            title="Use your generated invitations as the visual system."
            body="Instead of lifestyle placeholders, every major marketing surface should show the kinds of invitations your app can actually generate. That makes the promise immediately believable."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-[1fr_1fr_1fr]">
            {showcaseInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="rounded-[32px] border border-[#E8DDD1] bg-white p-5 shadow-soft transition hover:shadow-md hover:-translate-y-1"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#8A7A6A]">
                      {invitation.style}
                    </p>
                    <p className="mt-2 font-serif text-2xl text-[#211D1A]">
                      {invitation.couple_names.name1} & {invitation.couple_names.name2}
                    </p>
                  </div>
                  <Star size={18} className="text-[#C6A14B]" />
                </div>
                <InvitationPreview invitation={invitation} size="large" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="border-b border-[#E8E0D6] bg-white py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            eyebrow="A calmer create flow"
            title="Build the editor more like product onboarding than a long form."
            body="The next version of VowVisions should guide people through meaningful decisions in stages, with stronger hierarchy, less clutter, and a better relationship between controls and preview."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {processCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="rounded-[32px] border border-[#E7DACA] bg-[#FBF8F3] p-8 shadow-soft transition hover:shadow-md hover:-translate-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8A7A6A]">
                      {card.label}
                    </span>
                    <Icon size={18} className="text-[#C6A14B]" />
                  </div>
                  <h3 className="mt-8 font-serif text-3xl text-[#211D1A]">{card.title}</h3>
                  <p className="mt-5 text-base leading-8 text-[#6C6258]">{card.body}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/create"
              className="inline-flex items-center gap-2 rounded-full bg-[#1E1A17] px-7 py-4 text-base font-medium text-white transition hover:bg-[#0E0C0B] hover:scale-[1.02] active:scale-100"
            >
              Try the create flow <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Dashboard ── */}
      <section className="border-b border-[#E8E0D6] bg-[#F8F4EE] py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            eyebrow="Invitation dashboard"
            title="Rework the library into a Stitch-ready operations dashboard for saved invitations."
            body="Borrow the clarity of a modern dashboard, but make it invitation-native: status, export readiness, favorite work, and payment progression should feel easy to scan on desktop and mobile."
          />
          <div className="mt-14 grid gap-8 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="rounded-[36px] border border-[#E2D7CA] bg-white p-7 shadow-soft">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#8A7A6A]">Overview</p>
                  <h3 className="mt-2 font-serif text-3xl text-[#211D1A]">My Invitations</h3>
                </div>
                <div className="rounded-full bg-[#F3E8D3] p-3 text-[#8E6B1C]">
                  <Layers3 size={18} />
                </div>
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {dashboardStats.map((stat) => (
                  <div key={stat.label} className="rounded-[24px] bg-[#FBF8F3] p-4">
                    <p className="text-xs font-medium uppercase tracking-[0.24em] text-[#8A7A6A]">{stat.label}</p>
                    <p className="mt-3 font-serif text-2xl">{stat.value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 space-y-3">
                {dashboardRows.map((row) => (
                  <div
                    key={row.title}
                    className="flex items-start justify-between gap-4 rounded-[22px] border border-[#EEE4D8] p-4 transition hover:border-[#D4C5B0]"
                  >
                    <div>
                      <p className="font-medium text-[#211D1A]">{row.title}</p>
                      <p className="mt-1 text-sm text-[#8B7F73]">{row.style}</p>
                      <p className="mt-2 text-sm text-[#6C6258]">{row.detail}</p>
                    </div>
                    <span className="rounded-full bg-[#F6EFE4] px-3 py-1 text-xs font-medium text-[#8E6B1C]">
                      {row.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[36px] border border-[#E2D7CA] bg-[#1F1A16] p-7 text-white shadow-[0_32px_80px_-40px_rgba(20,14,10,0.45)]">
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#D8C5A2]">Checkout and handoff</p>
              <h3 className="mt-4 max-w-xl font-serif text-4xl leading-tight">
                A clearer final review flow that fits Stitch Express instead of feeling bolted on.
              </h3>
              <p className="mt-5 max-w-xl text-base leading-8 text-[#D5CEC7]">
                Keep the flow simple: selected format, payment method, delivery summary, then one strong final action. Revolut-inspired structure, invitation-specific content.
              </p>
              <div className="mt-10 grid gap-4 md:grid-cols-2">
                <div className="rounded-[26px] bg-white/[0.08] p-5">
                  <p className="text-xs font-medium uppercase tracking-[0.24em] text-[#D8C5A2]">Review card</p>
                  <p className="mt-3 text-lg font-medium">Signature plan</p>
                  <p className="mt-2 text-sm text-[#D5CEC7]">Story export, print PDF, saved to library</p>
                </div>
                <div className="rounded-[26px] bg-white/[0.08] p-5">
                  <p className="text-xs font-medium uppercase tracking-[0.24em] text-[#D8C5A2]">Payments</p>
                  <p className="mt-3 text-lg font-medium">Stitch Express</p>
                  <p className="mt-2 text-sm text-[#D5CEC7]">Simpler payment selection and post-payment confirmation</p>
                </div>
              </div>
              <div className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#F3E8D3] px-4 py-2 text-sm font-medium text-[#211D1A]">
                <ShieldCheck size={16} />
                Cleaner review, stronger trust, less friction
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing teaser ── */}
      <section className="border-b border-[#E8E0D6] bg-white py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            eyebrow="Pricing tone"
            title="Keep pricing separate so the home page stays invitation-first."
            body="View the dedicated pricing page for launch offers, monthly plans, and the countdown without crowding the landing experience."
            align="center"
          />
          <div className="mx-auto mt-12 max-w-4xl rounded-[36px] border border-[#E5D5BF] bg-[linear-gradient(180deg,#FFF9F1_0%,#F8EEDF_100%)] p-8 text-center shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A07B39]">Launch pricing available now</p>
            <h3 className="mt-4 font-serif text-4xl leading-tight text-[#211D1A]">
              See the full pricing page for first-month offers, regular rates, and the live countdown.
            </h3>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-[#6C6258]">
              Essential starts free for the first month, with Signature and Heirloom discounted as part of the current launch window.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1E1A17] px-7 py-4 text-base font-medium text-white transition hover:bg-[#0E0C0B] hover:scale-[1.02] active:scale-100"
              >
                View pricing <ArrowRight size={18} />
              </Link>
              <Link
                to="/create"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#DCCFBE] bg-white/85 px-7 py-4 text-base font-medium text-[#211D1A] transition hover:border-[#C7B49A] hover:bg-white hover:scale-[1.02] active:scale-100"
              >
                Start creating
              </Link>
            </div>
            {/* Trust micro-copy */}
            <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2">
              {trustSignals.map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5 text-xs text-[#8A7A6A]">
                  <Icon size={13} className="text-[#C6A14B]" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── App concept ── */}
      <section className="bg-[#FBF8F3] py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            eyebrow="iOS concept"
            title="A brief app direction that mirrors the new web tone."
            body="The app should feel crisp, quiet, and invitation-led. Wealthsimple calm for onboarding, a cleaner dashboard for saved invitations, and a softer premium pricing and checkout journey."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {appScreens.map((screen, index) => (
              <div
                key={screen.title}
                className="rounded-[36px] border border-[#E2D7CA] bg-white p-6 shadow-soft transition hover:shadow-md hover:-translate-y-1"
              >
                <div className="rounded-[28px] border border-[#EAE0D6] bg-[linear-gradient(180deg,#FEFCF8_0%,#F5EEE5_100%)] p-6">
                  <div className="mx-auto flex min-h-[360px] max-w-[250px] flex-col justify-between rounded-[32px] border border-[#DDD1C2] bg-white p-5 shadow-[0_18px_45px_-28px_rgba(28,20,14,0.35)]">
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#8A7A6A]">
                        Screen {index + 1}
                      </p>
                      <h3 className="mt-5 font-serif text-3xl leading-tight text-[#211D1A]">
                        {screen.title}
                      </h3>
                    </div>
                    <p className="text-sm leading-7 text-[#6C6258]">{screen.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
